import React, { useCallback, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import appApi from '../../../api/app-api';
import { AbstractProgram } from '../../../api/types/settings/studies/abstract-program';

interface AbstractProgramSelectionDialogProps {
  visible: boolean;
  selectedIds: number[];
  onHide: () => void;
  onApprove: (selectedIds: number[]) => void;
}

const ITEMS_PER_PAGE = 10;

export const AbstractProgramSelectionDialog: React.FC<
  AbstractProgramSelectionDialogProps
> = ({ visible, selectedIds, onHide, onApprove }) => {
  const [abstractPrograms, setAbstractPrograms] = useState<AbstractProgram[]>(
    [],
  );
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(ITEMS_PER_PAGE);

  const loadAbstractPrograms = useCallback(
    async (offset: number, limit: number) => {
      setTableLoading(true);
      try {
        const response = await appApi.getAbstractProgramsPaginated(
          offset,
          limit,
        );
        if (response.data.body) {
          setAbstractPrograms(response.data.body.data);
          setTotalRecords(response.data.body.total);
        }
      } catch (error) {
        console.error('Error loading abstract programs:', error);
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    setFirst(0);
    setRows(ITEMS_PER_PAGE);
    setSelectedProgramIds(selectedIds);
    loadAbstractPrograms(0, ITEMS_PER_PAGE);
  }, [visible, selectedIds, loadAbstractPrograms]);

  const onPageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
    loadAbstractPrograms(event.first, event.rows);
  };

  const handleToggleProgram = (programId: number) => {
    setSelectedProgramIds((prev) =>
      prev.includes(programId)
        ? prev.filter((id) => id !== programId)
        : [...prev, programId],
    );
  };

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <div style={{ color: '#4b5563', fontSize: '14px' }}>
        נבחרו: {selectedProgramIds.length}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button label="ביטול" severity="secondary" onClick={onHide} />
        <Button label="אישור" onClick={() => onApprove(selectedProgramIds)} />
      </div>
    </div>
  );

  return (
    <Dialog
      header="בחירת תוכניות אבסטרקטיות"
      visible={visible}
      onHide={onHide}
      style={{ width: '900px' }}
      modal
      footer={footer}
    >
      <DataTable
        value={abstractPrograms}
        dataKey="id"
        loading={tableLoading}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        tableStyle={{ direction: 'rtl' }}
      >
        <Column
          headerStyle={{ width: '60px' }}
          body={(rowData: AbstractProgram) => (
            <Checkbox
              checked={selectedProgramIds.includes(rowData.id)}
              onChange={() => handleToggleProgram(rowData.id)}
            />
          )}
        />
        <Column
          field="programName"
          header="שם תוכנית"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="years"
          header="מספר שנים"
          style={{ textAlign: 'right', width: '130px' }}
        />
      </DataTable>
    </Dialog>
  );
};
